// Username-prompt gate — the hidden watcher that opens AppUsernamePrompt
// exactly once for a freshly-joined invitee (INVITE-ONBOARDING-USERNAME-
// NOT-ORG-1). Sibling of NoOrgOverlay in shape and mount point: a
// display:none div whose only job is a reactive predicate.
//
// Fires when EITHER trigger holds:
//   • root._usernamePromptPending — raised by accept-invite's live
//     (no-reload) path right after the committed switch;
//   • the persisted PENDING key — written before accept-invite's
//     hard-reload fallback, so the nudge survives the reboot.
// Never fires once the DONE key exists (saved or explicitly kept), and
// never before the auth snapshot has seeded (same guard rationale as
// NoOrgOverlay's).

import {
  isUsernamePromptDone,
  isUsernamePromptPendingPersisted
} from '../functions/usernamePrompt.js'

const _shouldPrompt = (root) => {
  if (!root?._authReady || !root?.signedIn) return false
  if (isUsernamePromptDone()) return false
  // NEVER during a workspace/org switch: the accept flow raises the flag
  // right after sdk.switchOrg resolves, and the switch pipeline's state
  // rebuild (PHASE-1 wipe + refetch behind the loader) runs AFTER that —
  // a modal opened in that window is destroyed with the rebuild while the
  // flag has already been consumed (measured 2026-08-27: nudge never seen
  // after a live accept; opens fine once the switch settles). Wait for
  // the switch to finish; the subscription below re-evaluates then.
  if (root?._wsSwitching || root?._wpSwitching || root?._dataLoading) return false
  return !!root?._usernamePromptPending || isUsernamePromptPendingPersisted()
}

const _maybeOpen = (el, s) => {
  if (el.__usernamePromptOpened) return
  if (!_shouldPrompt(s.root)) return
  el.__usernamePromptOpened = true
  el.call('openUsernamePrompt')
}

export const UsernamePromptGate = {
  tag: 'div',
  display: 'none',
  subscribeTo: [
    '_usernamePromptPending',
    'signedIn',
    '_authReady',
    '_wsSwitching',
    '_wpSwitching',
    '_dataLoading'
  ],
  onCreate: (el, s) => _maybeOpen(el, s),
  onUpdate: (el, s) => _maybeOpen(el, s)
}
